/**
 * ComboBoxCellEditor
 *
 * A cell editor component that provides async search functionality with:
 * - Debounced search input
 * - Optional item grouping
 * - Support for updating multiple fields on selection
 * - Integration with React Spectrum ComboBox
 *
 * @example
 * // Basic usage
 * <ComboBoxCellEditor
 *   config={{
 *     loadItems: async (text) => fetchProducts(text),
 *     placeholder: 'Search...',
 *   }}
 *   value={currentValue}
 *   onChange={setValue}
 *   onCommit={commitEdit}
 *   onCancel={cancelEdit}
 *   row={currentRow}
 * />
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { ComboBox, Item, Section, Text } from "@adobe/react-spectrum";
import { useAsyncList } from "react-stately";
import { ComboBoxEditorConfig, ComboBoxItem, TreeGridRow } from "../types";
import styles from "../TreeGridComponent.module.css";

interface ComboBoxCellEditorProps<T = any> {
  /** ComboBox configuration */
  config: ComboBoxEditorConfig<T>;
  /** Current cell value */
  value: any;
  /** Display value for the input (may differ from stored value) */
  displayValue?: string;
  /** Validation error message */
  error?: string;
  /** Single value change callback */
  onChange: (value: any) => void;
  /** Update multiple fields at once */
  onMultiFieldChange?: (changes: Record<string, any>) => void;
  /** Commit changes and exit edit mode */
  onCommit: () => void;
  /** Cancel editing and revert changes */
  onCancel: () => void;
  /** The row being edited */
  row: TreeGridRow<T>;
  /** Whether this editor should auto-focus */
  shouldFocus?: boolean;
}

/** Internal type for grouped items */
interface GroupedItems {
  name: string;
  children: ComboBoxItem[];
}

export function ComboBoxCellEditor<T = any>({
  config,
  value,
  displayValue,
  error,
  onChange,
  onMultiFieldChange,
  onCommit,
  onCancel,
  row,
  shouldFocus = true,
}: ComboBoxCellEditorProps<T>) {
  const {
    loadItems,
    groupBy,
    placeholder = "Search...",
    menuWidth = 300,
    debounceMs = 200,
    minSearchLength = 0,
    // renderItem - available in config but not used here
    getDisplayValue,
    getValue,
    onSelectionChange,
    isRequired,
    commitOnSelection = false,
    syncCustomInputValue = false,
  } = config;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inputValue, setInputValue] = useState(
    displayValue || String(value || ""),
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle menu open/close state change
  const handleOpenChange = useCallback((isOpen: boolean) => {
    setIsMenuOpen(isOpen);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Async list for ComboBox - handles loading and filtering
  const list = useAsyncList<ComboBoxItem | GroupedItems>({
    async load({ filterText }) {
      const searchText = filterText ?? inputValue ?? "";

      // Don't search if below minimum length
      if (searchText.length < minSearchLength) {
        return { items: [], cursor: undefined };
      }

      return new Promise((resolve) => {
        // Clear existing debounce timer
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        // Debounce the API call
        debounceRef.current = setTimeout(async () => {
          try {
            const items = await loadItems(searchText);

            // Group items if groupBy function is provided
            if (groupBy) {
              const grouped = items.reduce(
                (acc, item) => {
                  const group = groupBy(item);
                  if (!acc[group]) {
                    acc[group] = [];
                  }
                  acc[group].push(item);
                  return acc;
                },
                {} as Record<string, ComboBoxItem[]>,
              );

              const groupedItems: GroupedItems[] = Object.entries(grouped).map(
                ([name, children]) => ({
                  name: `${children.length} ${name}`,
                  children,
                }),
              );

              resolve({ items: groupedItems, cursor: undefined });
            } else {
              resolve({ items, cursor: undefined });
            }
          } catch (err) {
            console.error("ComboBoxCellEditor load error:", err);
            resolve({ items: [], cursor: undefined });
          }
        }, debounceMs);
      });
    },
  });

  // Handle input text change
  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      setSelectedKey(null);
      if (syncCustomInputValue) {
        onChange(newValue);
      }
      list.setFilterText(newValue);
    },
    [list, onChange, syncCustomInputValue],
  );

  // Find item by key in potentially grouped list
  const findItemByKey = useCallback(
    (key: React.Key): ComboBoxItem | null => {
      for (const item of list.items) {
        if ("children" in item) {
          // Grouped item
          const found = (item as GroupedItems).children.find(
            (child) => child.id === key,
          );
          if (found) return found;
        } else if ((item as ComboBoxItem).id === key) {
          return item as ComboBoxItem;
        }
      }
      return null;
    },
    [list.items],
  );

  // Handle selection change
  const handleSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (!key) {
        // Selection cleared (user is typing) - don't clear the value
        // Only clear selectedKey for UI state, but don't call onChange(null)
        setSelectedKey(null);
        return;
      }

      const item = findItemByKey(key);
      if (!item) return;

      setSelectedKey(String(key));

      // Update display value - this is what shows in the ComboBox
      const display = getDisplayValue ? getDisplayValue(item) : item.label;
      setInputValue(display);

      // Get the value to store in the cell (could be id or display based on config)
      const newValue = getValue ? getValue(item) : display;

      // Handle multi-field updates if configured
      if (onSelectionChange) {
        const changes = onSelectionChange(item, row);
        if (changes && Object.keys(changes).length > 0) {
          if (onMultiFieldChange) {
            // Use multi-field change which includes all fields from selection
            onMultiFieldChange(changes as Record<string, any>);
          } else {
            // Fallback: just update current field
            onChange(newValue);
          }
        } else {
          // No multi-field changes returned, just update current field
          onChange(newValue);
        }
      } else {
        // No onSelectionChange configured, just update current field
        onChange(newValue);
      }

      if (commitOnSelection) {
        setTimeout(() => onCommit(), 0);
      }
    },
    [
      findItemByKey,
      getValue,
      getDisplayValue,
      onChange,
      onMultiFieldChange,
      onSelectionChange,
      row,
      commitOnSelection,
      onCommit,
    ],
  );

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === "Enter") {
        // Stop Enter from propagating to the document-level keyboard handler
        // This prevents the TreeGrid from committing the edit when user is
        // selecting from the ComboBox dropdown
        if (isMenuOpen) {
          // Menu is open - Enter is for selecting an item, stop propagation
          e.stopPropagation();
        } else if (selectedKey || inputValue) {
          // Menu is closed and user has made a selection - allow commit
          e.preventDefault();
          e.stopPropagation();
          onCommit();
        }
      }
    },
    [onCancel, onCommit, isMenuOpen, selectedKey, inputValue],
  );

  // Render grouped items with sections
  if (groupBy) {
    return (
      <div
        className={styles.comboBoxEditor}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <ComboBox
          aria-label="Search"
          width="100%"
          menuWidth={menuWidth}
          items={list.items as GroupedItems[]}
          inputValue={inputValue}
          selectedKey={selectedKey}
          onInputChange={handleInputChange}
          onSelectionChange={handleSelectionChange}
          onOpenChange={handleOpenChange}
          loadingState={list.loadingState}
          onLoadMore={list.loadMore}
          placeholder={placeholder}
          validationState={error ? "invalid" : undefined}
          isRequired={isRequired}
          autoFocus={shouldFocus}
          allowsCustomValue
        >
          {(group: GroupedItems) => (
            <Section key={group.name} items={group.children} title={group.name}>
              {(item: ComboBoxItem) => (
                <Item key={item.id} textValue={item.label}>
                  <Text>{item.label}</Text>
                  {item.unitPrice !== undefined && (
                    <Text slot="description">
                      {item.unitPrice.toLocaleString()} {item.currency || "CZK"}
                    </Text>
                  )}
                </Item>
              )}
            </Section>
          )}
        </ComboBox>
      </div>
    );
  }

  // Render flat list without sections
  return (
    <div
      className={styles.comboBoxEditor}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
    >
      <ComboBox
        aria-label="Search"
        width="100%"
        menuWidth={menuWidth}
        items={list.items as ComboBoxItem[]}
        inputValue={inputValue}
        selectedKey={selectedKey}
        onInputChange={handleInputChange}
        onSelectionChange={handleSelectionChange}
        onOpenChange={handleOpenChange}
        loadingState={list.loadingState}
        onLoadMore={list.loadMore}
        placeholder={placeholder}
        validationState={error ? "invalid" : undefined}
        isRequired={isRequired}
        autoFocus={shouldFocus}
        allowsCustomValue
      >
        {(item: ComboBoxItem) => (
          <Item key={item.id} textValue={item.label}>
            <Text>{item.label}</Text>
            {item.unitPrice !== undefined && (
              <Text slot="description">
                {item.unitPrice.toLocaleString()} {item.currency || "CZK"}
              </Text>
            )}
          </Item>
        )}
      </ComboBox>
    </div>
  );
}

export default ComboBoxCellEditor;
